import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Icons,
} from '@commenda/kakashi';
import { useEffect, useRef, useState } from 'react';

interface ExternalLinkModalProps {
  url: string;
  isOpen: boolean;
  onClose: () => void;
}

/** App-styled confirmation shown before opening an external link from chat markdown (replaces
 * streamdown's default warning). Offers copy-to-clipboard or open-in-new-tab. */
export function ExternalLinkModal({
  url,
  isOpen,
  onClose,
}: ExternalLinkModalProps) {
  const [copied, setCopied] = useState(false);
  const copyResetRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(copyResetRef.current), []);

  const handleCopy = () => {
    navigator.clipboard
      .writeText(url)
      .then(() => {
        setCopied(true);
        clearTimeout(copyResetRef.current);
        copyResetRef.current = setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => undefined);
  };

  const handleOpen = () => {
    window.open(url, '_blank', 'noopener,noreferrer');
    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="max-w-lg" preventCloseOnOutsideClick={false}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-md">
            <Icons.LinkExternal01 className="size-5 shrink-0 text-fg-tertiary" />
            Open external link?
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-lg p-3xl">
          <p className="txt-small-regular text-text-tertiary">
            You&apos;re about to visit an external website.
          </p>
          <div className="max-h-40 overflow-y-auto break-all rounded-md border border-border-primary bg-bg-secondary p-md font-mono txt-extra-small-regular text-text-secondary">
            {url}
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="secondary"
            size="md"
            leftIcon={<Icons.Copy01 className="size-4" />}
            onClick={handleCopy}
          >
            {copied ? 'Copied' : 'Copy link'}
          </Button>
          <Button
            variant="primary"
            size="md"
            leftIcon={<Icons.LinkExternal01 className="size-4" />}
            onClick={handleOpen}
          >
            Open link
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
